import { useEffect, useState } from "react";

import {
  adjustFirmAccountQuota,
  createFirmAccount,
  listFirmAccounts,
  resetFirmAccountPassword,
  updateFirmAccount,
} from "../api/client";
import type { CustomerRole, FirmAccount } from "../types/api";

type QuotaMode = "increase" | "decrease";
type QuotaStep = "edit" | "confirm";
const ACCOUNTS_PER_PAGE = 10;

export function paginateFirmAccounts<T>(accounts: T[], requestedPage: number) {
  const pageCount = Math.max(1, Math.ceil(accounts.length / ACCOUNTS_PER_PAGE));
  const page = Math.min(Math.max(1, requestedPage), pageCount);
  const start = (page - 1) * ACCOUNTS_PER_PAGE;
  return { items: accounts.slice(start, start + ACCOUNTS_PER_PAGE), page, pageCount };
}

export function QuotaAdjustmentDialog({
  account,
  step,
  mode,
  amount,
  error,
  onModeChange,
  onAmountChange,
  onNext,
  onBack,
  onConfirm,
  onClose,
}: {
  account: FirmAccount;
  step: QuotaStep;
  mode: QuotaMode;
  amount: string;
  error: string;
  onModeChange: (mode: QuotaMode) => void;
  onAmountChange: (amount: string) => void;
  onNext: () => void;
  onBack: () => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const numericAmount = Number(amount || 0);
  return (
    <div className="customer-payment-modal quota-modal" role="dialog" aria-modal="true">
      <section className="customer-card quota-dialog">
        <header><div><h2>{step === "edit" ? "调整律师额度" : "确认额度调整"}</h2><p>{account.name} · {account.mobile}</p></div><button type="button" className="quota-close" aria-label="关闭" onClick={onClose}>×</button></header>
        {step === "edit" ? <>
          <div className="quota-current"><div><span>已分配额度</span><strong>¥{account.allocated_amount.toFixed(2)}</strong></div><div><span>已用额度</span><strong>¥{account.used_amount.toFixed(2)}</strong></div><div><span>可用额度</span><strong>¥{account.spending_limit.toFixed(2)}</strong></div></div>
          <div className="quota-mode"><button type="button" className={mode === "increase" ? "is-active" : ""} onClick={() => onModeChange("increase")}>增加额度</button><button type="button" className={mode === "decrease" ? "is-active" : ""} onClick={() => onModeChange("decrease")}>收回额度</button></div>
          <label className="quota-amount">调整金额<input type="number" min="0.01" step="0.01" value={amount} placeholder="请输入金额" onChange={(event) => onAmountChange(event.target.value)} /></label>
          {error && <p className="form-error">{error}</p>}
          <footer><button type="button" className="is-cancel" onClick={onClose}>取消</button><button type="button" onClick={onNext}>下一步</button></footer>
        </> : <>
          <div className="quota-confirm-summary"><div><span>调整律师</span><strong>{account.name}</strong></div><div><span>调整方式</span><strong>{mode === "increase" ? "增加额度" : "收回额度"}</strong></div><div><span>调整金额</span><strong>¥{numericAmount.toFixed(2)}</strong></div><div><span>调整后可用额度</span><strong>¥{(account.spending_limit + (mode === "increase" ? numericAmount : -numericAmount)).toFixed(2)}</strong></div></div>
          {error && <p className="form-error">{error}</p>}
          <p className="quota-confirm-question">确认要按以上内容修改吗？</p>
          <footer><button type="button" className="is-cancel" onClick={onBack}>返回修改</button><button type="button" onClick={onConfirm}>确认调整</button></footer>
        </>}
      </section>
    </div>
  );
}

export function FirmAccounts({
  role,
  onQuotaChanged,
  unallocatedBalance = 0,
}: {
  role: CustomerRole;
  onQuotaChanged?: () => void;
  unallocatedBalance?: number;
}) {
  const [accounts, setAccounts] = useState<FirmAccount[]>([]);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [notice, setNotice] = useState("");
  const [page, setPage] = useState(1);
  const [temporaryPasswords, setTemporaryPasswords] = useState<Record<string, string>>({});
  const [quotaDialog, setQuotaDialog] = useState<{ account: FirmAccount; step: QuotaStep; mode: QuotaMode; amount: string; error: string } | null>(null);

  const reload = () => listFirmAccounts().then(setAccounts);
  useEffect(() => { if (role === "ORGANIZATION_ADMIN") void reload(); }, [role]);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(""), 1800);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const pagination = paginateFirmAccounts(accounts, page);
  useEffect(() => {
    if (pagination.page !== page) setPage(pagination.page);
  }, [page, pagination.page]);

  if (role !== "ORGANIZATION_ADMIN") return null;

  async function copyCredentials(account: FirmAccount) {
    let password = temporaryPasswords[account.id];
    if (!password) {
      const result = await resetFirmAccountPassword(account.id);
      password = result.initial_password;
      setTemporaryPasswords((current) => ({ ...current, [account.id]: password }));
    }
    await navigator.clipboard.writeText(`登录账号：${account.mobile}\n登录密码：${password}`);
    setNotice("账号密码已复制");
  }

  async function add(event: React.FormEvent) {
    event.preventDefault();
    const created = await createFirmAccount(name, mobile);
    setTemporaryPasswords((current) => ({
      ...current,
      [created.id]: created.initial_password,
    }));
    setNotice("律师账号已创建");
    setName("");
    setMobile("");
    const refreshedAccounts = await listFirmAccounts();
    setAccounts(refreshedAccounts);
    setPage(Math.max(1, Math.ceil(refreshedAccounts.length / ACCOUNTS_PER_PAGE)));
  }

  function reviewQuotaAdjustment() {
    if (!quotaDialog) return;
    const amount = Number(quotaDialog.amount);
    let error = "";
    if (!Number.isFinite(amount) || amount <= 0) error = "请输入大于 0 的调整金额";
    else if (quotaDialog.mode === "increase" && amount > unallocatedBalance) error = "调整金额不能超过律所待分配余额";
    else if (quotaDialog.mode === "decrease" && amount > quotaDialog.account.spending_limit) error = "收回金额不能超过律师当前可用额度";
    setQuotaDialog({ ...quotaDialog, error, step: error ? "edit" : "confirm" });
  }

  async function confirmQuotaAdjustment() {
    if (!quotaDialog) return;
    const amount = Number(quotaDialog.amount) * (quotaDialog.mode === "increase" ? 1 : -1);
    try {
      await adjustFirmAccountQuota(quotaDialog.account.id, amount);
      setQuotaDialog(null);
      await reload();
      onQuotaChanged?.();
      setNotice("额度已更新");
    } catch (reason) {
      setQuotaDialog({ ...quotaDialog, error: reason instanceof Error ? reason.message : "额度调整失败" });
    }
  }

  async function toggleStatus(account: FirmAccount) {
    await updateFirmAccount(account.id, account.status === "ACTIVE" ? "DISABLED" : "ACTIVE");
    await reload();
    setNotice(account.status === "ACTIVE" ? "账号已停用" : "账号已启用");
  }

  return (
    <section className="customer-card firm-account-card">
      <h2>律师账号管理</h2>
      <form className="firm-account-form" onSubmit={add}>
        <input required placeholder="律师姓名" value={name} onChange={(event) => setName(event.target.value)} />
        <input required placeholder="手机号" value={mobile} onChange={(event) => setMobile(event.target.value)} />
        <button type="submit">添加律师账号</button>
      </form>
      <div className="firm-account-list-frame">
        <div className="firm-account-table-wrap">
          <table className="firm-account-table">
            <thead><tr><th>姓名</th><th>登录账号</th><th>登录密码</th><th>已分配额度</th><th>已用额度</th><th>可用额度</th><th>状态</th><th>操作</th></tr></thead>
            <tbody>{pagination.items.map((account) => (
              <tr key={account.id}>
                <td>{account.name}</td>
                <td>{account.mobile}</td>
                <td><span className="password-mask">••••••••</span><button type="button" className="table-link" onClick={() => void copyCredentials(account)}>{temporaryPasswords[account.id] ? "复制账号密码" : "重置并复制"}</button></td>
                <td>¥{account.allocated_amount.toFixed(2)}</td>
                <td>¥{account.used_amount.toFixed(2)}</td>
                <td>¥{account.spending_limit.toFixed(2)}</td>
                <td><span className={`account-status ${account.status === "ACTIVE" ? "is-active" : "is-disabled"}`}>{account.status === "ACTIVE" ? "启用" : "停用"}</span></td>
                <td><div className="table-actions"><button type="button" onClick={() => setQuotaDialog({ account, step: "edit", mode: "increase", amount: "", error: "" })}>调整额度</button><button type="button" className="is-secondary" onClick={() => void toggleStatus(account)}>{account.status === "ACTIVE" ? "停用" : "启用"}</button></div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <nav className="firm-account-pagination" aria-label="律师账号分页">
          <span>共 {accounts.length} 条</span>
          <div>
            <button type="button" className="pagination-button" disabled={pagination.page === 1} onClick={() => setPage(pagination.page - 1)}>上一页</button>
            {Array.from({ length: pagination.pageCount }, (_, index) => index + 1).map((pageNumber) => <button type="button" key={pageNumber} className={`pagination-button pagination-number ${pageNumber === pagination.page ? "is-current" : ""}`} aria-current={pageNumber === pagination.page ? "page" : undefined} onClick={() => setPage(pageNumber)}>{pageNumber}</button>)}
            <button type="button" className="pagination-button" disabled={pagination.page === pagination.pageCount} onClick={() => setPage(pagination.page + 1)}>下一页</button>
          </div>
        </nav>
      </div>
      {notice && <div className="account-toast" role="status">{notice}</div>}
      {quotaDialog && <QuotaAdjustmentDialog account={quotaDialog.account} step={quotaDialog.step} mode={quotaDialog.mode} amount={quotaDialog.amount} error={quotaDialog.error} onModeChange={(mode) => setQuotaDialog({ ...quotaDialog, mode, error: "" })} onAmountChange={(amount) => setQuotaDialog({ ...quotaDialog, amount, error: "" })} onNext={reviewQuotaAdjustment} onBack={() => setQuotaDialog({ ...quotaDialog, step: "edit", error: "" })} onConfirm={() => void confirmQuotaAdjustment()} onClose={() => setQuotaDialog(null)} />}
    </section>
  );
}
